import React, { forwardRef, useState } from 'react';

import { MenuItem, Paper, Stack, TextField } from '@mui/material';

import {
  BANNER_SECTION_PALETTES,
  BANNER_SECTION_TAG_POSITION,
  BANNER_TAG_TYPE,
  BANNER_TAG_TYPES,
  DEFAULT_BANNER_SECTION_PALETTE,
  DEFAULT_BANNER_TAG_TYPE,
} from '~/consts/customerCommunication/heroPlanner';

import { DynamicSectionType } from '../../context/useDynamicSections';

import ScheduleHeaderImageInput, { parseHeaderImageFormData } from './Inputs/ScheduleHeaderImageInput';

export enum SCHEDULE_BANNER_SECTION_FORM_NAMES {
  TITLE = 'banner_section_title',
  ARTICLE_URL = 'banner_section_article_url',
  PALETTE = 'banner_section_palette',
  TAG = 'banner_section_tag',
  TAG_TYPE = 'banner_section_tag_type',
  CTA_LABEL = 'banner_section_cta_label',
  SUBTEXT = 'banner_section_subtext',
}

interface Props {
  existingBannerName?: string;
  existingBannerUrl?: string;
  existingWebImageId?: string;
  existingMobileImageId?: string;
  existingSubtext?: string;
  existingCtaLabel?: string;
  existingPalette?: CustomerCommunication.BannerSectionPalette;
  existingTag?: CustomerCommunication.BannerSectionTag;
}

// banner content shared by the custom schedule banner section and the hero deals text banner
export function parseBannerFormData(formData: FormData): CustomerCommunication.HeroDealsBanner {
  const imageData = parseHeaderImageFormData(formData);
  const subtext = formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.SUBTEXT) as string;
  const ctaLabel = formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.CTA_LABEL) as string;
  const palette = formData.get(
    SCHEDULE_BANNER_SECTION_FORM_NAMES.PALETTE,
  ) as CustomerCommunication.BannerSectionPalette;
  const tagText = (formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.TAG) as string)?.trim();
  const tag: CustomerCommunication.BannerSectionTag | undefined = tagText
    ? {
        position: BANNER_SECTION_TAG_POSITION,
        bannerTagText: tagText,
        bannerTagType: formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.TAG_TYPE) as BANNER_TAG_TYPE,
      }
    : undefined;
  return {
    bannerName: String(formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.TITLE)),
    bannerUrl: String(formData.get(SCHEDULE_BANNER_SECTION_FORM_NAMES.ARTICLE_URL)),
    desktopImageId: imageData.webImageId,
    mobileImageId: imageData.mobileImageId,
    // the API rejects empty strings for these optional fields, so leave them out when blank
    subtext: subtext || undefined,
    ctaLabel: ctaLabel || undefined,
    palette: palette || undefined,
    // the API accepts a list of tags, but the admin only allows one per banner
    tags: tag ? [tag] : undefined,
  };
}

export function parseBannerSectionFormData(formData: FormData, position = -1) {
  return {
    type: DynamicSectionType.BANNER,
    ...parseBannerFormData(formData),
    position,
  };
}

const ScheduleBannerSectionForm = forwardRef<HTMLFormElement, Props>((props, ref) => {
  const {
    existingBannerName = '',
    existingBannerUrl = '',
    existingWebImageId = '',
    existingMobileImageId = '',
    existingSubtext = '',
    existingCtaLabel = '',
    existingPalette = DEFAULT_BANNER_SECTION_PALETTE,
    existingTag,
  } = props;

  const [bannerName, setBannerName] = useState(existingBannerName);
  const [bannerUrl, setBannerUrl] = useState(existingBannerUrl);
  const [subtext, setSubtext] = useState(existingSubtext);
  const [ctaLabel, setCtaLabel] = useState(existingCtaLabel);
  const [palette, setPalette] = useState<string>(existingPalette);
  const [tagText, setTagText] = useState(existingTag?.bannerTagText ?? '');
  const [tagType, setTagType] = useState<BANNER_TAG_TYPE>(
    (existingTag?.bannerTagType as BANNER_TAG_TYPE) ?? DEFAULT_BANNER_TAG_TYPE,
  );
  const [webImageId, setWebImageId] = useState(existingWebImageId);
  const [mobileImageId, setMobileImageId] = useState(existingMobileImageId);
  const [headerUrl, setHeaderUrl] = useState(''); // This is required by ScheduleHeaderImageInput but not used in banner

  return (
    <Stack component="form" ref={ref} spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label="Variant"
            name={SCHEDULE_BANNER_SECTION_FORM_NAMES.PALETTE}
            value={palette}
            onChange={(e) => setPalette(e.target.value)}
          >
            {BANNER_SECTION_PALETTES.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          {/* 70/30 splits match the web/mobile columns in ScheduleHeaderImageInput */}
          <Stack direction="row" spacing={2}>
            <Stack width="70%">
              <TextField
                fullWidth
                label="Banner Heading"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.TITLE}
                value={bannerName}
                onChange={(e) => setBannerName(e.target.value)}
                required
              />
            </Stack>
            <Stack width="30%">
              <TextField
                fullWidth
                label="Subtext"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.SUBTEXT}
                value={subtext}
                onChange={(e) => setSubtext(e.target.value)}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Stack width="70%">
              <TextField
                fullWidth
                label="Banner Tag"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.TAG}
                value={tagText}
                onChange={(e) => setTagText(e.target.value)}
              />
            </Stack>
            <Stack width="30%">
              <TextField
                select
                fullWidth
                label="Tag Style"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.TAG_TYPE}
                value={tagType}
                onChange={(e) => setTagType(e.target.value as BANNER_TAG_TYPE)}
                disabled={!tagText.trim()}
              >
                {BANNER_TAG_TYPES.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Stack width="70%">
              <TextField
                fullWidth
                label="Redirect URL"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.ARTICLE_URL}
                value={bannerUrl}
                onChange={(e) => setBannerUrl(e.target.value)}
                required
              />
            </Stack>
            <Stack width="30%">
              <TextField
                fullWidth
                label="CTA Label"
                name={SCHEDULE_BANNER_SECTION_FORM_NAMES.CTA_LABEL}
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
              />
            </Stack>
          </Stack>

          <ScheduleHeaderImageInput
            webImageId={webImageId}
            mobileImageId={mobileImageId}
            headerUrl={headerUrl}
            onWebImageIdChange={setWebImageId}
            onMobileImageIdChange={setMobileImageId}
            onHeaderUrlChange={setHeaderUrl}
            showHeaderUrl={false}
          />
        </Stack>
      </Paper>
    </Stack>
  );
});

ScheduleBannerSectionForm.displayName = 'ScheduleBannerSectionForm';
export default ScheduleBannerSectionForm;
